import pickBy from 'lodash/pickBy'
import {
  GeckoAssetModelType,
  GeckoLeakDetectionValveJSON,
  GeckoTubePlateJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  GeckoAssetModel,
  GeckoComponentModelJSON,
  GeckoFeaturesJSON,
  regenerateBaseAssetModel,
} from 'types/entries/geckoAssetModel'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { RolodexEntryPutJson } from 'types/rolodex/rolodexEntries'
import { assetModelPrecision } from 'utils/constants'
import { COORDINATE_PAIR_REGEX } from 'utils/zodHelpers'

import { BaseAssetModelData } from './baseAssetModelData'
import { GeckoFixedSensorJSON } from './features/geckoFixedSensor'
import { GeckoManwayJSON } from './features/geckoManway'
import { GeckoNozzleJSON } from './features/geckoNozzle'
import { GeckoPlateJSON } from './features/geckoPlate'
import { GeckoPolygonalPlateJSON } from './features/geckoPolygonalPlate'
import { GeckoConeCourseJSON } from './features/legacyCone/geckoConeCourse'
import { GeckoConicalPlateJSON } from './features/legacyCone/geckoConicalPlate'
import { GeckoCourseJSON } from './features/verticalTankShell/geckoShellCourse'
import { GeckoConeCircumferentialWeldJSON } from './features/welds/geckoConeCircumferentialWeld'
import { GeckoConeRadialWeldJSON } from './features/welds/geckoConeRadialWeld'
import { GeckoHorizontalWeldJSON } from './features/welds/geckoHorizontalWeld'
import { GeckoVerticalWeldJSON } from './features/welds/geckoVerticalWeld'
import { regenerateNuclearContainmentDomeAssetModel } from './geckoDomeAssetModel'
import { regenerateLegacyBoilerWallsAssetModel } from './geckoLegacyBoilerAssetModel'
import { regenerateMachinedForgingAssetModel } from './geckoMachinedForgingAssetModel'
import { regeneratePressureVesselAssetModel } from './geckoPressureVesselAssetModel'
import { regenerateShipAssetModel } from './geckoShipAssetModel'
import {
  convertConePolygonsToWeldMap,
  convertCoursesToPolygons,
  convertPlatesToPolygons,
  convertPolygonsToWeldMap,
  WeldedPolygon,
  WrappingDirection,
} from './generators/weldGenerator'

export enum InsulatedOptions {
  Yes = 'Yes',
  No = 'No',
  Partial = 'Partial',
  Unknown = 'Unknown',
}

export enum ContainmentOptions {
  PRIMARY = 'Primary',
  SECONDARY = 'Secondary',
  NONE = 'None',
}

export enum HasReinforcementPadsOptions {
  Yes = 'Yes',
  No = 'No',
  Unknown = 'Unknown',
}

export enum WeldedToShellOptions {
  Yes = 'Yes',
  No = 'No',
  Unknown = 'Unknown',
}

export function organizeFeaturesByType(
  componentJSON: GeckoComponentModelJSON,
  componentFeatureTypes: GeckoFeatureModelType[]
): Record<string, Record<string, GeckoFeaturesJSON>> {
  const organizedFeatureJSONs: Record<
    string,
    Record<string, GeckoFeaturesJSON>
  > = {}

  if (componentJSON && componentJSON.features) {
    for (const [featureID, featureJSON] of Object.entries(
      componentJSON.features
    )) {
      // If the Feature Type has already been parsed out, add this feature to the map
      if (featureJSON.type in organizedFeatureJSONs) {
        organizedFeatureJSONs[featureJSON.type][featureID] = featureJSON
      } else {
        // If the Feature Type has not been seen, double check that it's an allowed type for this Component Type
        if (
          componentFeatureTypes.includes(
            featureJSON.type as GeckoFeatureModelType
          )
        ) {
          organizedFeatureJSONs[featureJSON.type as string] = {
            [featureID]: featureJSON,
          }
        }
      }
    }
  }

  return organizedFeatureJSONs
}

export function getAssetModelTypeName(
  assetModelType: GeckoAssetModelType
): string {
  switch (assetModelType) {
    case GeckoAssetModelType.VerticalTank:
      return 'Tank'
    case GeckoAssetModelType.HorizontalTank:
      return 'Horizontal Tank'
    case GeckoAssetModelType.Piping:
      return 'Pipeline'
    case GeckoAssetModelType.LegacyBoiler:
      return 'Boiler'
    case GeckoAssetModelType.Ship:
      return 'Ship'
    case GeckoAssetModelType.MissileSilo:
      return 'Missile Silo'
    case GeckoAssetModelType.NuclearContainmentDome:
      return 'Nuclear Containment Dome'
    case GeckoAssetModelType.MachinedForging:
      return 'Machined Forging'
    case GeckoAssetModelType.PressureVessel:
      return 'Pressure Vessel'
    case GeckoAssetModelType.Hood:
      return 'Hood'
    case GeckoAssetModelType.Fan:
      return 'Fan'
    case GeckoAssetModelType.Blower:
      return 'Blower'
    case GeckoAssetModelType.Motor:
      return 'Motor'
    case GeckoAssetModelType.Pump:
      return 'Pump'
    case GeckoAssetModelType.Valve:
      return 'Valve'
  }
}

export function convertCoordinateString(
  coordinateString: string,
  currentUnits: GeckoMeasurementUnit,
  displayUnit: GeckoMeasurementUnit
): string | null {
  if (!COORDINATE_PAIR_REGEX.test(coordinateString)) {
    return null
  }
  const coordinates = coordinateString.match(COORDINATE_PAIR_REGEX)

  if (coordinates) {
    const convertedCoordinates = coordinates.map((coordinatePair) => {
      const [x, y] = coordinatePair.slice(1, -1).split(',').map(Number)
      const convertedX = GeckoMeasurement.CreateFromValue(x, currentUnits)
        .convertTo(displayUnit)
        .toPrecision(assetModelPrecision).num

      const convertedY = GeckoMeasurement.CreateFromValue(y, currentUnits)
        .convertTo(displayUnit)
        .toPrecision(assetModelPrecision).num

      return `[${convertedX}, ${convertedY}]`
    })

    return convertedCoordinates.join(', ')
  }

  return null
}

export function organizeFeatures(features: Record<string, GeckoFeaturesJSON>): {
  plates: Record<string, GeckoPlateJSON>
  courses: Record<string, GeckoCourseJSON>
  verticalWelds: Record<string, GeckoVerticalWeldJSON>
  horizontalWelds: Record<string, GeckoHorizontalWeldJSON>
  manways: Record<string, GeckoManwayJSON>
  nozzles: Record<string, GeckoNozzleJSON>
  leakDetectionValves: Record<string, GeckoLeakDetectionValveJSON>
  fixedSensors: Record<string, GeckoFixedSensorJSON>
  polygonalPlates: Record<string, GeckoPolygonalPlateJSON>
} {
  const result: {
    plates: Record<string, GeckoPlateJSON>
    courses: Record<string, GeckoCourseJSON>
    verticalWelds: Record<string, GeckoVerticalWeldJSON>
    horizontalWelds: Record<string, GeckoHorizontalWeldJSON>
    manways: Record<string, GeckoManwayJSON>
    nozzles: Record<string, GeckoNozzleJSON>
    leakDetectionValves: Record<string, GeckoLeakDetectionValveJSON>
    fixedSensors: Record<string, GeckoFixedSensorJSON>
    polygonalPlates: Record<string, GeckoPolygonalPlateJSON>
  } = {
    plates: {},
    courses: {},
    verticalWelds: {},
    horizontalWelds: {},
    manways: {},
    nozzles: {},
    leakDetectionValves: {},
    fixedSensors: {},
    polygonalPlates: {},
  }

  Object.entries(features).forEach(([id, feature]) => {
    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        result.plates[id] = feature as GeckoPlateJSON
        break
      case GeckoFeatureModelType.Course:
        result.courses[id] = feature as GeckoCourseJSON
        break
      case GeckoFeatureModelType.VerticalWeld:
        result.verticalWelds[id] = feature as GeckoVerticalWeldJSON
        break
      case GeckoFeatureModelType.HorizontalWeld:
        result.horizontalWelds[id] = feature as GeckoHorizontalWeldJSON
        break
      case GeckoFeatureModelType.Manway:
        result.manways[id] = feature as GeckoManwayJSON
        break
      case GeckoFeatureModelType.Nozzle:
        result.nozzles[id] = feature as GeckoNozzleJSON
        break
      case GeckoFeatureModelType.LeakDetectionValve:
        result.leakDetectionValves[id] = feature as GeckoLeakDetectionValveJSON
        break
      case GeckoFeatureModelType.FixedSensor:
        result.fixedSensors[id] = feature as GeckoFixedSensorJSON
        break
      case GeckoFeatureModelType.PolygonalPlate:
        result.polygonalPlates[id] = feature as GeckoPolygonalPlateJSON
        break
    }
  })

  return result
}

export function regenerateAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  if (
    assetModel.data.type === GeckoAssetModelType.VerticalTank ||
    assetModel.data.type === GeckoAssetModelType.HorizontalTank
  ) {
    return regenerateTankAssetModel(assetModel)
  } else if (assetModel.data.type === GeckoAssetModelType.Piping) {
    return regeneratePipingAssetModel(assetModel)
  } else if (assetModel.data.type === GeckoAssetModelType.LegacyBoiler) {
    return regenerateLegacyBoilerWallsAssetModel(assetModel)
  } else if (assetModel.data.type === GeckoAssetModelType.Ship) {
    return regenerateShipAssetModel(assetModel)
  } else if (
    assetModel.data.type === GeckoAssetModelType.NuclearContainmentDome
  ) {
    return regenerateNuclearContainmentDomeAssetModel(assetModel)
  } else if (assetModel.data.type === GeckoAssetModelType.MachinedForging) {
    return regenerateMachinedForgingAssetModel(assetModel)
  } else if (assetModel.data.type === GeckoAssetModelType.PressureVessel) {
    return regeneratePressureVesselAssetModel(assetModel)
  } else if (assetModel.data.type === GeckoAssetModelType.Hood) {
    return regenerateHoodAssetModel(assetModel)
  } else {
    return regenerateBaseAssetModel(assetModel)
  }
}

export function regenerateTankAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const rawAssetModel = assetModel.raw

  if (!rawAssetModel.data.components) {
    throw Error(
      'Unable to regenerate this Asset Model! Components seems to be undefined.'
    )
  }

  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.VerticalTankShell:
        regeneratedComponents[componentID] =
          regenerateVerticalTankShellComponent(component)
        break
      case GeckoComponentModelType.HorizontalTankShell:
        regeneratedComponents[componentID] =
          regenerateHorizontalTankShellComponent(component)
        break
      case GeckoComponentModelType.TankFloor:
        regeneratedComponents[componentID] =
          regenerateTankFloorComponent(component)
        break
      case GeckoComponentModelType.TankFlatRoof:
        regeneratedComponents[componentID] =
          regenerateTankFlatRoofComponent(component)
        break
      case GeckoComponentModelType.LegacyCone:
        regeneratedComponents[componentID] =
          regenerateLegacyConeComponent(component)
        break
      case GeckoComponentModelType.Cone:
        regeneratedComponents[componentID] = regenerateConeComponent(component)
        break
      case GeckoComponentModelType.VerticalTankFoundation:
        regeneratedComponents[componentID] = component
        break
      default:
        throw Error('Unable to determine this type of Tank Component!')
    }
  }

  assetModel.raw.data.components = regeneratedComponents

  return assetModel.raw
}

export function regenerateVerticalTankShellComponent(
  verticalTankShell: Record<string, unknown>
): Record<string, unknown> {
  const tankFeatures = verticalTankShell.features as Record<
    string,
    | GeckoPlateJSON
    | GeckoCourseJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
    | GeckoPolygonalPlateJSON
  >
  // If there's no features on the asset model, there's nothing to regenerate
  if (Object.keys(tankFeatures).length === 0) {
    return verticalTankShell
  }

  const tankPlates = new Map<string, GeckoPlateJSON>()
  const tankCourses = new Map<string, GeckoCourseJSON>()
  const tankPolygonalPlates = new Map<string, GeckoPolygonalPlateJSON>()
  const tankOther = new Map<
    string,
    GeckoManwayJSON | GeckoNozzleJSON | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(tankFeatures)) {
    if (!feature.type) {
      console.error(
        `Unable to regenerate some features, as feature.type is missing!`
      )
    }

    // When filtering through the existing features, we only need plates separated so that we can recalculate welds
    // We should throw away an existing welds and regenerated them from plates
    // We will likely refactor this eventually as we allow the addition of vertical welds, and effectively we will
    // then be throwing away plates (as they will be recalculated from courses & vertical welds)
    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        tankPlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.Course:
        tankCourses.set(featureID, feature as GeckoCourseJSON)
        break
      case GeckoFeatureModelType.PolygonalPlate:
        tankPolygonalPlates.set(featureID, feature as GeckoPolygonalPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        tankOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = verticalTankShell.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    verticalTankShell.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertCoursesToPolygons(
    [...tankCourses.values()],
    componentBounds,
    polygonArray
  )
  convertPlatesToPolygons(
    [...tankPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
    | GeckoPlateJSON
    | GeckoCourseJSON
    | GeckoPolygonalPlateJSON
  >([
    ...tankPlates.entries(),
    ...welds.entries(),
    ...tankCourses.entries(),
    ...tankPolygonalPlates.entries(),
    ...tankOther.entries(),
  ])

  verticalTankShell.features = Object.fromEntries(features) as Record<
    string,
    unknown
  >

  return verticalTankShell
}

export function regenerateTankFloorComponent(
  tankFloor: Record<string, unknown>
): Record<string, unknown> {
  const tankFeatures = tankFloor.features as Record<
    string,
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >
  // If there's no features on the asset model, there's nothing to regenerate
  if (Object.keys(tankFeatures).length === 0) {
    return tankFloor
  }

  const tankPlates = new Map<string, GeckoPlateJSON>()
  const tankOther = new Map<
    string,
    | GeckoCourseJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(tankFeatures)) {
    if (!feature.type) {
      console.error(
        `Unable to regenerate some features, as feature.type is missing!`
      )
    }

    // When filtering through the existing features, we only need plates separated so that we can recalculate welds
    // We should throw away an existing welds and regenerated them from plates
    // We will likely refactor this eventually as we allow the addition of vertical welds, and effectively we will
    // then be throwing away plates (as they will be recalculated from courses & vertical welds)
    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        tankPlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        tankOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = tankFloor.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    tankFloor.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.None
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...tankPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
    | GeckoPlateJSON
    | GeckoCourseJSON
  >([...tankPlates.entries(), ...welds.entries(), ...tankOther.entries()])

  tankFloor.features = Object.fromEntries(features) as Record<string, unknown>

  return tankFloor
}

export function regenerateTankFlatRoofComponent(
  tankRoof: Record<string, unknown>
): Record<string, unknown> {
  const tankFeatures = tankRoof.features as Record<
    string,
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >
  // If there's no features on the asset model, there's nothing to regenerate
  if (Object.keys(tankFeatures).length === 0) {
    return tankRoof
  }

  const tankPlates = new Map<string, GeckoPlateJSON>()
  const tankOther = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(tankFeatures)) {
    if (!feature.type) {
      console.error(
        `Unable to regenerate some features, as feature.type is missing!`
      )
    }

    // When filtering through the existing features, we only need plates separated so that we can recalculate welds
    // We should throw away an existing welds and regenerated them from plates
    // We will likely refactor this eventually as we allow the addition of vertical welds, and effectively we will
    // then be throwing away plates (as they will be recalculated from courses & vertical welds)
    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        tankPlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        tankOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = tankRoof.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    tankRoof.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.None
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...tankPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
    | GeckoPlateJSON
  >([...tankPlates.entries(), ...welds.entries(), ...tankOther.entries()])

  tankRoof.features = Object.fromEntries(features) as Record<string, unknown>

  return tankRoof
}

// TODO: ENT-1465 Add more functionality when cones support features
export function regenerateLegacyConeComponent(
  legacyCone: Record<string, unknown>
): Record<string, unknown> {
  const coneFeatures = legacyCone.features as Record<
    string,
    | GeckoConicalPlateJSON
    | GeckoConeCourseJSON
    | GeckoConeCircumferentialWeldJSON
    | GeckoConeRadialWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >
  // If there's no features on the asset model, there's nothing to regenerate
  if (Object.keys(coneFeatures).length === 0) {
    return legacyCone
  }

  const conePlates = new Map<string, GeckoConicalPlateJSON>()
  const coneCourses = new Map<string, GeckoConeCourseJSON>()
  const coneOther = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoConeCourseJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(coneFeatures)) {
    if (!feature.type) {
      console.error(
        `Unable to regenerate some features, as feature.type is missing!`
      )
    }

    // When filtering through the existing features, we only need plates separated so that we can recalculate welds
    // We should throw away an existing welds and regenerated them from plates
    // We will likely refactor this eventually as we allow the addition of vertical welds, and effectively we will
    // then be throwing away plates (as they will be recalculated from courses & vertical welds)
    switch (feature.type) {
      case GeckoFeatureModelType.ConePlate:
        conePlates.set(featureID, feature as GeckoConicalPlateJSON)
        break
      case GeckoFeatureModelType.ConeCourse:
        coneCourses.set(featureID, feature as GeckoConeCourseJSON)
        break
      case GeckoFeatureModelType.ConeCircumferentialWeld:
      case GeckoFeatureModelType.ConeRadialWeld:
        continue
      default:
        coneOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = legacyCone.component_bounds as Record<
    string,
    number
  >
  // This is due to the fact that cones are in a circular plane and only have y start and y end
  const componentBounds = GeckoComponentRect.CreateFromXYs(
    legacyCone.id as string,
    0,
    componentBoundsJSON.y_start as number,
    360,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Degrees,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertCoursesToPolygons(
    [...coneCourses.values()],
    componentBounds,
    polygonArray
  )
  convertPlatesToPolygons(
    [...conePlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertConePolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoConeCircumferentialWeldJSON
    | GeckoConeRadialWeldJSON
    | GeckoConeCourseJSON
    | GeckoConicalPlateJSON
  >([...coneCourses.entries(), ...conePlates.entries(), ...welds.entries()])

  legacyCone.features = Object.fromEntries(features) as Record<string, unknown>

  return legacyCone
}

export function regenerateConeComponent(
  cone: Record<string, unknown>
): Record<string, unknown> {
  const coneFeatures = cone.features as Record<
    string,
    | GeckoConicalPlateJSON
    | GeckoConeCourseJSON
    | GeckoConeCircumferentialWeldJSON
    | GeckoConeRadialWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >
  // If there's no features on the asset model, there's nothing to regenerate
  if (Object.keys(coneFeatures).length === 0) {
    return cone
  }

  const conePlates = new Map<string, GeckoConicalPlateJSON>()
  const coneCourses = new Map<string, GeckoConeCourseJSON>()
  const coneOther = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoConeCourseJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(coneFeatures)) {
    if (!feature.type) {
      console.error(
        `Unable to regenerate some features, as feature.type is missing!`
      )
    }

    // When filtering through the existing features, we only need plates separated so that we can recalculate welds
    // We should throw away an existing welds and regenerated them from plates
    // We will likely refactor this eventually as we allow the addition of vertical welds, and effectively we will
    // then be throwing away plates (as they will be recalculated from courses & vertical welds)
    switch (feature.type) {
      case GeckoFeatureModelType.ConePlate:
        conePlates.set(featureID, feature as GeckoConicalPlateJSON)
        break
      case GeckoFeatureModelType.ConeCourse:
        coneCourses.set(featureID, feature as GeckoConeCourseJSON)
        break
      case GeckoFeatureModelType.ConeCircumferentialWeld:
      case GeckoFeatureModelType.ConeRadialWeld:
        continue
      default:
        coneOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = cone.component_bounds as Record<string, number>
  // This is due to the fact that cones are in a circular plane and only have y start and y end
  const componentBounds = GeckoComponentRect.CreateFromXYs(
    cone.id as string,
    0,
    componentBoundsJSON.y_start as number,
    360,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Degrees,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertCoursesToPolygons(
    [...coneCourses.values()],
    componentBounds,
    polygonArray
  )
  convertPlatesToPolygons(
    [...conePlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertConePolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoConeCircumferentialWeldJSON
    | GeckoConeRadialWeldJSON
    | GeckoConeCourseJSON
    | GeckoConicalPlateJSON
  >([...coneCourses.entries(), ...conePlates.entries(), ...welds.entries()])

  cone.features = Object.fromEntries(features) as Record<string, unknown>

  return cone
}

export function regeneratePipingAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const rawAssetModel = assetModel.raw

  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }

  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.PipeSection:
        regeneratedComponents[componentID] =
          regeneratePipeSectionComponent(component)
        break
      case GeckoComponentModelType.PipeElbow:
        regeneratedComponents[componentID] = regeneratePipeElbow(component)
        break
      case GeckoComponentModelType.PipeTJoint:
      case GeckoComponentModelType.PipeEccentricReducer:
        regeneratedComponents[componentID] = component
        break
      default:
        throw new Error('Unable to determine this type of Piping Component!')
    }
  }

  assetModel.raw.data.components = regeneratedComponents

  return assetModel.raw
}

export function regeneratePipeElbow(
  pipeSection: Record<string, unknown>
): Record<string, unknown> {
  const pipeFeatures = pipeSection.features as Record<
    string,
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >

  // If there are no features on the asset model, there's nothing to regenerate
  if (Object.keys(pipeFeatures).length === 0) {
    return pipeSection
  }

  const pipePlates = new Map<string, GeckoPlateJSON>()
  const pipeOther = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(pipeFeatures)) {
    if (!feature.type) {
      console.error(
        'Unable to regenerate some features, as feature.type is missing!'
      )
      continue
    }

    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        pipePlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        pipeOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = pipeSection.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    pipeSection.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...pipePlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
    | GeckoPlateJSON
    | GeckoCourseJSON
  >([...pipePlates.entries(), ...welds.entries(), ...pipeOther.entries()])

  pipeSection.features = Object.fromEntries(features) as Record<string, unknown>

  return pipeSection
}

export function regeneratePipeSectionComponent(
  pipeSection: Record<string, unknown>
): Record<string, unknown> {
  const pipeFeatures = pipeSection.features as Record<
    string,
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >

  // If there are no features on the asset model, there's nothing to regenerate
  if (Object.keys(pipeFeatures).length === 0) {
    return pipeSection
  }

  const pipePlates = new Map<string, GeckoPlateJSON>()
  const pipeOther = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(pipeFeatures)) {
    if (!feature.type) {
      console.error(
        'Unable to regenerate some features, as feature.type is missing!'
      )
      continue
    }

    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        pipePlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        pipeOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = pipeSection.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    pipeSection.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...pipePlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
    | GeckoPlateJSON
    | GeckoCourseJSON
  >([...pipePlates.entries(), ...welds.entries(), ...pipeOther.entries()])

  pipeSection.features = Object.fromEntries(features) as Record<string, unknown>

  return pipeSection
}

export function regenerateHorizontalTankShellComponent(
  horizontalTankShell: Record<string, unknown>
): Record<string, unknown> {
  // TODO : don't want it to generate plates...etc. until the wrapping function is fixed

  const tankFeatures = horizontalTankShell.features as Record<
    string,
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >

  // If there's no features on the asset model, there's nothing to regenerate
  if (Object.keys(tankFeatures).length === 0) {
    return horizontalTankShell
  }

  const tankPlates = new Map<string, GeckoPlateJSON>()
  const tankOther = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
  >()

  for (const [featureID, feature] of Object.entries(tankFeatures)) {
    if (!feature.type) {
      console.error(
        `Unable to regenerate some features, as feature.type is missing!`
      )
    }

    // When filtering through the existing features, we only need plates separated so that we can recalculate welds
    // We should throw away an existing welds and regenerated them from plates
    // We will likely refactor this eventually as we allow the addition of vertical welds, and effectively we will
    // then be throwing away plates (as they will be recalculated from courses & vertical welds)
    // also note that horiztonal tank shells do not have shell courses as of right now
    switch (feature.type) {
      case GeckoFeatureModelType.Plate:
        tankPlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        tankOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = horizontalTankShell.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    horizontalTankShell.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.Y
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...tankPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoManwayJSON
    | GeckoNozzleJSON
    | GeckoFixedSensorJSON
    | GeckoPlateJSON
  >([...tankPlates.entries(), ...welds.entries(), ...tankOther.entries()])

  horizontalTankShell.features = Object.fromEntries(features) as Record<
    string,
    unknown
  >
  return horizontalTankShell
}

export function regenerateBasicShipHullComponent(
  shipHull: Record<string, unknown>
): Record<string, unknown> {
  // Since we don't support any features yet, we can just return the ship hull
  return shipHull
}

/**
 * @param assetModels
 * @returns Sorted Asset Models by effective date
 */
export function getSortedAssetModels(
  assetModels: ReadonlyArray<GeckoAssetModel<BaseAssetModelData>>
): GeckoAssetModel[] {
  return assetModels.toSorted(
    (a, b) => b.data.effective_date.getTime() - a.data.effective_date.getTime()
  )
}

export function regenerateHoodAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const rawAssetModel = assetModel.raw
  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }
  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.HoodStraight:
        regeneratedComponents[componentID] = regenerateHoodComponent(
          component,
          GeckoMeasurementUnit.Tubes
        )
        break
      case GeckoComponentModelType.HoodBend:
        regeneratedComponents[componentID] = regenerateHoodComponent(
          component,
          GeckoMeasurementUnit.Meters
        )
        break
      default:
        throw new Error('Unable to determine this type of Hood Component!')
    }
  }
  assetModel.raw.data.components = regeneratedComponents
  return assetModel.raw
}

export function regenerateHoodComponent(
  assetModel: Record<string, unknown>,
  xUnit: GeckoMeasurementUnit
): Record<string, unknown> {
  const hoodFeatures = assetModel.features as Record<
    string,
    | GeckoTubePlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPlateJSON
  >

  // If there are no features on the asset model, there's nothing to regenerate
  if (Object.keys(hoodFeatures).length === 0) {
    return assetModel
  }

  const hoodPlates = new Map<string, GeckoTubePlateJSON | GeckoPlateJSON>()
  const hoodOther = new Map<
    string,
    GeckoVerticalWeldJSON | GeckoHorizontalWeldJSON
  >()

  for (const [featureID, feature] of Object.entries(hoodFeatures)) {
    if (!feature.type) {
      console.error(
        'Unable to regenerate some features, as feature.type is missing!'
      )
      continue
    }

    switch (feature.type) {
      case GeckoFeatureModelType.TubePlate:
      case GeckoFeatureModelType.Plate:
        hoodPlates.set(featureID, feature)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue
      default:
        hoodOther.set(featureID, feature)
    }
  }

  const componentBoundsJSON = assetModel.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    assetModel.id as string,
    componentBoundsJSON.x_start ?? (componentBoundsJSON.tube_start as number),
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end ?? (componentBoundsJSON.tube_end as number),
    componentBoundsJSON.y_end as number,
    xUnit,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.X
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...hoodPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoTubePlateJSON
    | GeckoPlateJSON
  >([...hoodPlates.entries(), ...welds.entries(), ...hoodOther.entries()])

  assetModel.features = Object.fromEntries(features) as Record<string, unknown>

  return assetModel
}

export function extractFeatureMap<T>(
  featureData: Record<string, unknown>,
  type: GeckoFeatureModelType,
  createFn: (filtered: unknown) => Map<string, T>
) {
  const filtered = pickBy(featureData, (child) =>
    child && typeof child === 'object' && 'type' in child
      ? child.type === type
      : false
  )
  return createFn(filtered)
}
